import * as React from 'react';
import { Mapping } from '../../api/Mapping';
import { getBillingInformation, getEvent, setBillingInformation, setEventCategories } from '../../api/GraphProvider';
import { Abo } from '../../api/Abo';
import { IAbo } from '../../api/model/IAbo';
import { LinkCard } from './LinkCard';
import { Stack } from '@fluentui/react/lib/Stack';
import { getEventId, removeCategory, removeProperties, save, setCategory, setProperties } from '../../service/appointment.service';
import { showMessage } from '../../service/globalState.service';
import { addRecentlyLinked } from '../../service/recentlyLinked.service';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
} from "@fluentui/react-components";
import { ChevronDownRegular, ChevronRightRegular } from "@fluentui/react-icons";

const RECENT_EXPANDED_KEY = 'allocatus_recent_expanded';

const { useState, useRef, useEffect } = React;

interface AboListProps {
  abos: IAbo[];
  recentlyLinked?: IAbo[];
}

export const AboList: React.FC<AboListProps> = ({ abos, recentlyLinked = [] }) => {
  const [linkedAbo, setLinkedAboState] = useState<IAbo>(null);
  const linkedAboRef = useRef<IAbo>(null);
  const [canLink, setCanLink] = useState(true);
  const [pendingUnlink, setPendingUnlink] = useState<IAbo | null>(null);
  const [recentExpanded, setRecentExpanded] = useState<boolean>(() => {
    const stored = localStorage.getItem(RECENT_EXPANDED_KEY);
    return stored === null ? true : stored === 'true';
  });

  const toggleRecentExpanded = () => {
    setRecentExpanded(prev => {
      const next = !prev;
      localStorage.setItem(RECENT_EXPANDED_KEY, String(next));
      return next;
    });
  };

  const setLinkedAbo = (abo: IAbo) => {
    linkedAboRef.current = abo;
    setLinkedAboState(abo);
  };

  const _eventId = useRef<string>();
  const _appGuid = useRef<string>();
  const _app = useRef<Office.AppointmentCompose>();

  useEffect(() => {
    const { item } = Office.context.mailbox;
    _app.current = item;
    checkLinkedStatus();
    checkIfLinkable();
  }, [])

  const checkLinkedStatus = async () => {
    _eventId.current = await getEventId();
    _appGuid.current = await getBillingInformation(_eventId.current);
    if (!_appGuid.current) return;

    const mapping = await Mapping.get(_appGuid.current);
    const linked = abos.find(a => a.Id === mapping.aboGuid) || await Abo.get(mapping.aboGuid);

    if (linked) setLinkedAbo(linked);
  }

  const checkIfLinkable = () => {
    if (!_app.current) return;
    _app.current.recurrence.getAsync(r => {
      if (r.value) setCanLink(false);
    })

    _app.current.requiredAttendees.getAsync(r => {
      if (r.value.length > 1) setCanLink(false);
    });

    _app.current.optionalAttendees.getAsync(r => {
      if (r.value.length > 0) setCanLink(false);
    })
  }

  const linkAbo = async (abo: IAbo) => {
    if (!_app.current || !_eventId.current) return;

    if (_appGuid.current) {
      await Mapping.update({
        appGuid: _appGuid.current,
        aboGuid: abo.Id
      });
    } else {
      _appGuid.current = await Mapping.create({
        aboGuid: abo.Id
      });
      await setBillingInformation(_eventId.current, _appGuid.current);
    }

    // setCategory is best-effort via Office JS — don't abort if it fails
    try { await setCategory(); } catch (e) { console.warn('setCategory (Office JS) failed:', e); }
    await setProperties(abo.Id, abo.ProjectGuid, abo.TaskGuid, _appGuid.current);
    await getEvent(_eventId.current);
    await save();
    // Patch categories via Graph after save — Office JS removeAsync/addAsync in
    // compose mode does not reliably persist when saveAsync is called
    try { await setEventCategories(_eventId.current, ['Allocatus']); } catch (e) { console.warn('setEventCategories (Graph) failed:', e); }
    setLinkedAbo(abo);
    addRecentlyLinked(abo);
  }

  const unlinkAbo = async () => {
    if (!_app.current || !_eventId.current) return;
    await setBillingInformation(_eventId.current, '');
    _appGuid.current = '';
    // removeCategory is best-effort via Office JS — don't abort if it fails
    try { await removeCategory(); } catch (e) { console.warn('removeCategory (Office JS) failed:', e); }
    await removeProperties();
    await save();
    // Patch categories to empty via Graph after save — Office JS removeAsync in
    // compose mode does not reliably persist when saveAsync is called
    try { await setEventCategories(_eventId.current, []); } catch (e) { console.warn('setEventCategories (Graph) failed:', e); }
    setLinkedAbo(null);
  }

  const handleClick = async (abo: IAbo) => {
    // Read from ref so we always get the latest linkedAbo, avoiding stale closure issues
    const isLinked = linkedAboRef.current && linkedAboRef.current.Id === abo.Id;
    try {
      if (isLinked) {
        setPendingUnlink(abo);
        return;
      }
      await linkAbo(abo);
    } catch (e) {
      showMessage({
        type: "error",
        text: 'Error while linking task: ' + JSON.stringify(e)
      });
    }
  };

  const confirmUnlink = async () => {
    try {
      await unlinkAbo();
    } catch (e) {
      showMessage({
        type: "error",
        text: 'Error while unlinking task: ' + JSON.stringify(e)
      });
    } finally {
      setPendingUnlink(null);
    }
  };

  const renderAbo = (abo: IAbo): JSX.Element => {
    const isLinked = !!(linkedAbo && linkedAbo.Id === abo.Id);
    return <LinkCard
      key={abo.Id}
      task={abo}
      isLinked={isLinked}
      disabled={!canLink}
      onClick={() => handleClick(abo)}
    />;
  };

  const recentIds = new Set(recentlyLinked.map(a => a.Id));
  const mainAbos = abos.filter(a => !recentIds.has(a.Id));

  return (
    <>
      {recentlyLinked.length > 0 && (
        <>
          <div
            onClick={toggleRecentExpanded}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
              fontWeight: 600,
              color: '#888',
              padding: '4px 0 2px',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            {recentExpanded ? <ChevronDownRegular fontSize={12} /> : <ChevronRightRegular fontSize={12} />}
            Recently linked
          </div>
          {recentExpanded && (
            <Stack tokens={{ childrenGap: 10 }}>
              {recentlyLinked.map(renderAbo)}
            </Stack>
          )}
          {mainAbos.length > 0 && (
            <div style={{ fontSize: 12, fontWeight: 600, color: '#888', padding: '8px 0 2px' }}>
              All tasks
            </div>
          )}
        </>
      )}
      <Stack tokens={{ childrenGap: 10 }}>
        {mainAbos.map(renderAbo)}
      </Stack>

      <Dialog
        open={!!pendingUnlink}
        onOpenChange={(_, data) => { if (!data.open) setPendingUnlink(null); }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Unlink task?</DialogTitle>
            <DialogContent>
              Are you sure you want to unlink <strong>{pendingUnlink?.TaskName}</strong> from this event?
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setPendingUnlink(null)}>Cancel</Button>
              <Button appearance="primary" onClick={confirmUnlink}>Unlink</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}
